import type { Option } from '@byecode/ui'
import { IconFont, pointer, Select } from '@byecode/ui'
import type { DataSourceAbstract, TypeInstanceMap } from '@lighthouse/core'
import {
    DEFAULT_VALUE_VARIABLE,
    FieldTypeTag,
    getFieldIcon,
    getJoinOption,
    innerTypeJoinOperatorMap,
    JoinDataSourceDisableInnerType,
    VariableSelect
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

const SCxContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`

const SCxRemove = styled.div`
    ${pointer}
    padding: 2px;
    color: var(--color-gray-500);
    &:hover {
        color: var(--color-gray-900);
    }
`

const SelectStyle = {
    flex: 1,
    flexShrink: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap'
}

export interface MatchConditionProps {
    index: number
    primaryDsId: string
    prefixName: string
    joinedDs: DataSourceAbstract
    useDsIds: string[][]
    dataSourceList: DataSourceAbstract[]
    onRemove?: () => void
}

export const MatchCondition: React.FC<MatchConditionProps> = ({
    index,
    primaryDsId,
    prefixName,
    useDsIds,
    joinedDs,
    dataSourceList,
    onRemove
}) => {
    const { control, setValue } = useFormContext()

    const leftFieldId = useWatch({
        control,
        name: `${prefixName}.leftFieldId`
    })

    const operator = useWatch({
        control,
        name: `${prefixName}.operator`
    })

    const { schema: joinedDsSchema } = joinedDs
    const leftField = useMemo(() => {
        return joinedDsSchema?.[leftFieldId]
    }, [leftFieldId, joinedDsSchema])

    const operatorOptions = useMemo(() => {
        if (!leftField) {
            return []
        }
        const { innerType } = leftField
        if (!innerType) {
            return []
        }
        return innerTypeJoinOperatorMap[innerType]
    }, [leftField])

    const useJoinDsId = useMemo(() => {
        const ids = useDsIds.filter((_, i) => index > i).flat()
        return [primaryDsId, ...ids]
    }, [index, primaryDsId, useDsIds])

    const rightDataSourceOptions = useMemo(
        () =>
            useJoinDsId.reduce<DataSourceAbstract[]>((acc, cur) => {
                const dataSource = dataSourceList.find(item => item.id === cur)
                if (!dataSource) {
                    return acc
                }
                acc.push(dataSource)
                return acc
            }, []),
        [dataSourceList, useJoinDsId]
    )

    const leftFieldOptions = useMemo(() => {
        return Object.entries(joinedDsSchema).reduce<Option[]>((prev, [_, item]) => {
            if (!item?.innerType || JoinDataSourceDisableInnerType.includes(item.innerType)) {
                return prev
            }
            prev.push({
                label: item.name,
                value: item.id,
                icon: getFieldIcon(item.id, item.type, item.innerType),
                extra: <FieldTypeTag type={item.type} innerType={item.innerType} />
            })
            return prev
        }, [])
    }, [joinedDsSchema])

    const rightOptions = useMemo(
        () =>
            getJoinOption({
                primaryDsId,
                dataSourceList: rightDataSourceOptions
            }),
        [primaryDsId, rightDataSourceOptions]
    )

    const rightInnerTypes: TypeInstanceMap[] = useMemo(() => {
        if (!leftField) {
            return []
        }

        if (leftField.innerType === 'TEXT' && ['containedBy'].includes(operator)) {
            return ['TEXT', 'ARRAY']
        }

        if (leftField.innerType === 'ARRAY' && ['contains', 'notContains', 'containedBy'].includes(operator)) {
            return ['TEXT', 'NUMBER', 'ARRAY']
        }
        return [leftField.innerType]
    }, [leftField, operator])

    return (
        <SCxContainer>
            <Controller
                control={control}
                name={`${prefixName}.leftFieldId`}
                render={({ field }) => (
                    <Select
                        styles={{
                            root: SelectStyle
                        }}
                        searchable
                        dropdownWidth={282}
                        position="bottom-start"
                        value={field.value}
                        options={leftFieldOptions}
                        onChange={val => {
                            const oldField = joinedDsSchema?.[field.value]
                            const newField = joinedDsSchema?.[val]
                            field.onChange(val)
                            if (oldField?.innerType === newField.innerType) {
                                setValue(`${prefixName}.operator`, '=')
                            } else {
                                setValue(`${prefixName}.right`, DEFAULT_VALUE_VARIABLE)
                                setValue(`${prefixName}.operator`, '')
                            }
                        }}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${prefixName}.operator`}
                render={({ field }) => (
                    <Select
                        styles={{
                            root: {
                                flexShrink: 0,
                                width: '100px'
                            }
                        }}
                        disabled={!leftField}
                        value={field.value}
                        options={operatorOptions}
                        onChange={val => {
                            field.onChange(val)
                        }}
                    />
                )}
            />
            {!['isEmpty', 'isNotEmpty'].includes(operator) && (
                <Controller
                    control={control}
                    name={`${prefixName}.right`}
                    render={({ field }) => (
                        <VariableSelect
                            innerTypes={rightInnerTypes}
                            disabled={!leftField}
                            withinPortal
                            field={leftField}
                            disablePexels
                            datasourceOptions={rightOptions}
                            style={SelectStyle as React.CSSProperties}
                            width="100%"
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            )}
            <SCxRemove onClick={onRemove}>
                <IconFont type="Close" size={16} />
            </SCxRemove>
        </SCxContainer>
    )
}
